/**
 * Lightweight SFX player for the FishPrawnCrab sounds in
 *   public/assets/fishprawncrab_sfx/
 *
 * Clips are addressed by base file name (no extension) — `play('ClickBetSelect')`
 * plays `/assets/fishprawncrab_sfx/ClickBetSelect.mp3`. The path convention matches
 * the Rive layer (absolute `/assets/...`, see riveLayer.ts).
 *
 * Design notes:
 *  - One cached HTMLAudioElement per clip, cloned on play so rapid/overlapping
 *    triggers (e.g. quick chip clicks) don't cut each other off.
 *  - Browsers block audio until the page has a user gesture. `unlock()` primes
 *    every preloaded clip on the first pointer/key event so even the first
 *    programmatic play is allowed.
 *  - A missing file fails soft: the 404 is logged once and the clip becomes a
 *    no-op instead of throwing on every trigger.
 */
import { debugLog, warn } from '../util/logger';

const SFX_BASE = '/assets/fishprawncrab_sfx/';

export interface SoundManagerOptions {
  /** Master enable flag. Defaults to true. */
  enabled?: boolean;
  /** Playback volume 0..1. Defaults to 1. */
  volume?: number;
}

export class SoundManager {
  private readonly clips = new Map<string, HTMLAudioElement>();
  private readonly broken = new Set<string>();
  private enabled: boolean;
  private volume: number;
  private unlocked = false;

  constructor(opts: SoundManagerOptions = {}) {
    this.enabled = opts.enabled ?? true;
    this.volume = opts.volume ?? 1;
  }

  /** Eagerly create + start loading the given clips (base names, no extension). */
  preload(names: readonly string[]): void {
    for (const name of names) this.ensure(name);
  }

  setEnabled(on: boolean): void {
    this.enabled = on;
  }

  /**
   * Prime every known clip during a user gesture so later programmatic plays
   * aren't blocked by the browser autoplay policy. Bound so it can be passed
   * straight to addEventListener; idempotent (only the first call does work).
   */
  unlock = (): void => {
    if (this.unlocked) return;
    this.unlocked = true;
    for (const audio of this.clips.values()) {
      const p = audio.play();
      if (p && typeof p.then === 'function') {
        p.then(() => {
          audio.pause();
          audio.currentTime = 0;
        }).catch(() => {
          /* still locked — the first real play() will try again */
        });
      }
    }
  };

  /** Play a clip by base name. No-op when disabled or the file is missing. */
  play(name: string): void {
    if (!this.enabled || this.broken.has(name)) return;
    const base = this.ensure(name);
    // Clone so overlapping triggers each get their own playback head; the
    // src is served from the HTTP cache after preload, so this is cheap.
    const node = base.cloneNode(true) as HTMLAudioElement;
    node.volume = this.volume;
    const p = node.play();
    if (p && typeof p.then === 'function') {
      p.catch((e) => debugLog('[Sound] play blocked/failed', name, e));
    }
  }

  private ensure(name: string): HTMLAudioElement {
    const existing = this.clips.get(name);
    if (existing) return existing;
    const audio = new Audio(`${SFX_BASE}${name}.mp3`);
    audio.preload = 'auto';
    audio.addEventListener('error', () => {
      if (this.broken.has(name)) return;
      this.broken.add(name);
      warn(`[Sound] missing or unplayable clip: ${SFX_BASE}${name}.mp3`);
    });
    this.clips.set(name, audio);
    return audio;
  }
}
